import { Physics, Spine, Vector2 } from '@esotericsoftware/spine-pixi-v8';
import { MagicLayout } from '@magic/layout';
import type { ReelController } from 'controllers/stateControllers/Reel.controller';
import type { WinPosition } from '../../utils/api.types';

/** The only animation the payline export ships: draws the line in and pulses it. */
const showAnimation = 'show';
/** Spine the paylines are parented to — the one holding the reels. */
const boardSpineID = 'reels';
/** Prefix of the export's cell anchors: `point1`…`point5`, one per reel. */
const pointBone = 'point';
/** Bone every spine puts at its own origin — a symbol's is the centre of its cell. */
const originBone = 'root';
/** How many cells the export can anchor. */
const maxPoints = 5;

/**
 * Draws the lines a round won on, straight from the backend's win positions.
 *
 * The `payline` export is a whole line in a single skeleton: `point1`…`point5`
 * anchor it to a cell per reel, and every segment, glow and lead bone is IK- or
 * path-constrained onto those points. So a line is shaped by moving those five
 * bones and nothing else — one instance per entry in a reveal's `wins`, with its
 * points on that win's own `positions`. No line ID and no stored line shape are
 * involved: the reveal already says which cells paid.
 *
 * The instances are cloned when the result lands and destroyed on the next spin.
 * A clone has to be parented here — the layout walks the scene once, at manifest
 * load, adding every spine that nothing nests, so anything made later is
 * registered but never rendered until it gets a parent.
 */
export class PaylineController {
    /** The layout's own instance, kept as a hidden template to clone from. */
    private source: Spine;
    /**
     * What the lines hang off. They sit on it at identity, which makes a point in
     * its local space theirs too — and, because spine-pixi renders Y-down, the
     * same space their bone world positions are in.
     */
    private board: Spine;
    /** Instances on the board, by the layout ID they are registered under. */
    private shown: Map<string, Spine> = new Map();
    /** Only ever counts up, so a destroyed instance's ID is never handed out twice. */
    private created = 0;

    constructor(
        private layout: MagicLayout,
        private reels: ReelController,
    ) {
        const source = this.layout.spines.get('payline');

        if (!source) {
            throw new Error(`Payline spine not found`);
        }

        // nothing nests the export, so the layout parked it at the scene root,
        // where its setup pose draws a stray line across the corner. It is only
        // ever a template — the lines the player sees are clones of it.
        source.visible = false;
        this.source = source;

        const board = this.layout.spines.get(boardSpineID);

        if (!board) {
            throw new Error(`Board spine "${boardSpineID}" not found`);
        }

        this.board = board;
    }

    /**
     * Puts one line on the board per winning line and draws them all in.
     * Replaces whatever was showing, so replaying a reveal is safe. Resolves
     * once the lines have finished drawing.
     */
    async show(wins: { positions: WinPosition[] }[]) {
        this.clear();
        wins.forEach((win) => this.add(win.positions));
        await this.replay();
    }

    /** Draws the lines already on the board in again, leaving them in place. */
    async replay() {
        await Promise.all(
            [...this.shown.keys()].map((id) =>
                // solo: an instance only ever plays this one animation, and solo
                // playback skips the "already running" guard that would drop a
                // replay landing on a line still drawing
                this.layout.animations.play(id, showAnimation, true),
            ),
        );
    }

    /** Takes every line off the board and out of the layout's registries. */
    clear() {
        this.shown.forEach((payline, id) => this.remove(id, payline));
        this.shown.clear();
    }

    /** Clones one line, shapes it to the cells that paid, and parents it to the board. */
    private add(positions: WinPosition[]) {
        if (!positions.length) return;

        const id = `payline_${++this.created}`;
        const payline = this.layout.spine.clone(this.source, id);

        // clone() offsets a copy by 100px so a bare clone shows up beside its
        // source, and the source itself is hidden. This one is placed by its
        // bones, and sitting on the board at identity is what makes a
        // board-local point usable as a bone position.
        payline.position.set(0, 0);
        payline.scale.set(1);
        payline.rotation = 0;
        payline.visible = true;

        if (!this.placePoints(payline, positions)) {
            this.remove(id, payline);
            return;
        }

        // added last, so the line draws over the symbols it runs through
        this.board.addChild(payline);
        this.shown.set(id, payline);
    }

    /** Destroys one instance and drops it from the layout's registries. */
    private remove(id: string, payline: Spine) {
        payline.destroy(); // Container.destroy() unparents it too
        this.layout.spines.delete(id);
        this.layout.animations.unregisterSpine(id);
    }

    /**
     * Moves `point1`…`point5` onto the cells the win paid on, dragging the
     * constrained rig — segments, glows, leads — along with them. Returns false
     * when a cell has no symbol to sit on, so the caller can drop a line it
     * cannot draw instead of leaving it half-placed.
     */
    private placePoints(payline: Spine, positions: WinPosition[]): boolean {
        // worldToParent reads the parent bone's world transform, and a freshly
        // cloned skeleton has not computed one yet
        payline.skeleton.updateWorldTransform(Physics.update);

        const cells = positions.slice(0, maxPoints);
        let last: Vector2 | null = null;

        for (let index = 0; index < maxPoints; index++) {
            const cell = cells[index];
            // a win pays on as few as three reels, so the points past its last
            // cell are parked on that cell — their segments then have no length
            // to draw, and `trimPoints` hides what would still show
            const position: Vector2 | null = cell ? this.getCellPosition(cell) : last;
            const bone = payline.skeleton.findBone(`${pointBone}${index + 1}`);

            if (!position || !bone) return false;

            // spine-pixi renders Y-down (it sets `Skeleton.yDown`), so a bone's
            // world position lives in the same space as the board's local
            // coordinates — only the parent transform has to be undone to turn
            // the point into the local pose the bone is driven by. Copied first:
            // worldToParent rewrites the vector it is handed.
            const local = bone.pose.worldToParent(new Vector2(position.x, position.y));

            bone.pose.setPosition(local.x, local.y);

            if (cell) last = position;
        }

        this.trimPoints(payline, cells.length);

        return true;
    }

    /**
     * Hides the parts of the rig past the win's last cell, which would otherwise
     * draw stacked on the final symbol. `show` keys an attachment timeline on
     * every slot, so clearing attachments or fading them does not stick —
     * deactivating the bone a slot hangs from is what keeps it off screen.
     *
     * The line meshes are the exception: all four hang off the root bone, so
     * they cannot be hidden per segment. Parking the unused points on the last
     * cell (see `placePoints`) is what keeps them from drawing — their path
     * segments collapse to nothing.
     */
    private trimPoints(payline: Spine, used: number) {
        for (let point = used + 1; point <= maxPoints; point++) {
            // the point sprite hangs off `p<n>_S`, its glow off `p<n>_glow`
            this.deactivateBone(payline, `p${point}_S`);
            this.deactivateBone(payline, `p${point}_glow`);
        }

        // `lead<n>` is the arrow arriving at point n+1, so the last used point
        // is also the last one that should be arrived at
        for (let lead = used; lead < maxPoints; lead++) {
            this.deactivateBone(payline, `lead${lead}`);
        }
    }

    /**
     * Drops a bone out of rendering. Safe to leave set: the active flags are
     * recomputed by `updateCache`, which a posed-and-played instance never
     * triggers, and the instance is destroyed at the end of the spin anyway.
     */
    private deactivateBone(payline: Spine, boneName: string) {
        const bone = payline.skeleton.findBone(boneName);

        if (bone) bone.active = false;
    }

    /**
     * Where a win position sits in the board's space, taken off the symbol
     * filling that cell — so the points land on the symbols that actually paid.
     * Positions are in the round's own board convention, the same one the win
     * symbol animations resolve through.
     */
    private getCellPosition(cell: WinPosition): Vector2 | null {
        const symbolID = this.reels.getSymbolIDByPosition(cell.reel, cell.row);
        // a symbol rides its reel's slot bone, so its origin is the cell centre
        // and only means anything globally
        const global = symbolID
            ? this.layout.spine.getBoneGlobalPosition(symbolID, originBone)
            : undefined;

        if (!global) {
            // the usual cause is the round's board and its win positions
            // disagreeing about the hidden buffer rows, which lands the point on
            // a slot outside the visible window — so say which slot it resolved
            // to rather than only that it failed
            const { offset, firstSlot, lastSlot } = this.reels.rowMapping;

            console.warn(
                `Payline: no symbol to anchor at reel ${cell.reel}, row ${cell.row} — ` +
                    `resolved to slot ${cell.row + offset} (row offset ${offset}, ` +
                    `visible slots ${firstSlot}-${lastSlot})`,
            );

            return null;
        }

        // the board resolves the global point back into its own space
        const { x, y } = this.board.toLocal(global);

        return new Vector2(x, y);
    }
}
